import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import profileClientIcon from '../../assets/img3d/profileClient.png';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from '../../hooks/use-toast';
import { 
  User, Mail, Phone, MapPin, Calendar, Clock,
  CheckCircle, XCircle, MessageSquare, ArrowLeft, Home,
  Building2, TrendingUp, AlertCircle, Edit, Save, Bot,
  Check, X
} from 'lucide-react';
import axios from 'axios';
import { cn } from "@/lib/utils";
import { messagingService } from '../../services/messagingService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalLeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // États d'action
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    fetchLead();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API}/professional/leads/${id}`);
      const leadData = response.data;
      
      // Normalisation des données pour éviter les problèmes de casse
      const normalizedLead = {
        ...leadData,
        seekerName: leadData.seekerName || leadData.seeker_name,
        seekerEmail: leadData.seekerEmail || leadData.seeker_email,
        seekerPhone: leadData.seekerPhone || leadData.seeker_phone,
        seekerMessage: leadData.seekerMessage || leadData.seeker_message,
        status: leadData.status,
        professionalStatus: leadData.professionalStatus || leadData.professional_status || 'PENDING',
        visitScheduled: leadData.visitScheduled || leadData.visit_scheduled,
        visitDate: leadData.visitDate || leadData.visit_date,
        visitNotes: leadData.visitNotes || leadData.visit_notes,
        visitStatus: leadData.visitStatus || leadData.visit_status || 'NONE',
        // On simule une analyse IA si elle n'existe pas
        aiAnalysis: leadData.aiAnalysis || "Ce prospect semble très intéressé par les biens avec extérieur. Budget cohérent avec le marché local. À contacter rapidement.",
        aiScore: leadData.aiScore || 85
      };

      setLead(normalizedLead);
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger le lead', variant: 'destructive' });
      navigate('/professional/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus, reason = '') => {
    if (responding) return;
    try {
      setResponding(true);
      const action = newStatus === 'ACCEPTED' ? 'accept' : 'reject';
      await messagingService.respondToAssignment(
        id,
        action,
        reason || 'Mise à jour statut',
      );
      toast({ title: 'Succès', description: `Lead ${newStatus === 'ACCEPTED' ? 'accepté' : 'refusé'}` });
      fetchLead();
    } catch (e) {
      toast({ title: 'Erreur', description: "Action impossible", variant: 'destructive' });
    } finally {
      setResponding(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);

  if (loading) return (
    <Layout headerConfig={{ icon: profileClientIcon, iconColor: 'bg-white', title: 'Chargement...' }}>
      <div className="flex justify-center h-96 items-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9F68]"></div></div>
    </Layout>
  );

  if (!lead) return null;

  const property = lead.property || {};
  const professionalStatus = (lead.professionalStatus || '').toString().toUpperCase();
  const isPending = professionalStatus === 'PENDING';
  const isAccepted = professionalStatus === 'ACCEPTED';
  const isRefused = professionalStatus === 'REFUSED';
  const visitStatus = (lead.visitStatus || '').toString().toUpperCase();

  const formatDateTime = (dateValue) => {
    if (!dateValue) return null;
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return null;
    return {
      date: d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    };
  };

  const timelineSteps = [
    {
      key: 'received',
      label: 'Lead reçu',
      icon: CheckCircle,
      color: 'text-blue-600',
      at: formatDateTime(lead.createdAt || lead.created_at)
    },
    {
      key: 'assigned',
      label: 'Demande assignée',
      icon: Home,
      color: 'text-orange-600',
      at: formatDateTime(
        lead.assignedAt ||
        lead.assigned_at ||
        lead.assignedDate ||
        lead.assigned_date ||
        lead.createdAt ||
        lead.created_at
      )
    },
    {
      key: 'pending',
      label: 'En attente de réponse',
      icon: Clock,
      color: 'text-gray-600',
      at: isPending ? formatDateTime(lead.statusUpdatedAt || lead.status_updated_at || lead.updatedAt || lead.updated_at) : null
    },
    {
      key: 'accepted',
      label: 'Lead accepté',
      icon: CheckCircle,
      color: 'text-green-600',
      at: isAccepted ? formatDateTime(lead.acceptedAt || lead.accepted_at || lead.statusUpdatedAt || lead.status_updated_at || lead.updatedAt || lead.updated_at) : null
    },
    {
      key: 'pending_admin_visit',
      label: "En attente de planification (Admin)",
      icon: Calendar,
      color: 'text-amber-600',
      at: (isAccepted && visitStatus === 'PENDING_ADMIN')
        ? formatDateTime(
            lead.professionalResponseDate ||
            lead.professional_response_date ||
            lead.statusUpdatedAt ||
            lead.status_updated_at ||
            lead.updatedAt ||
            lead.updated_at
          )
        : null
    },
    {
      key: 'refused',
      label: 'Lead refusé',
      icon: XCircle,
      color: 'text-red-600',
      at: isRefused ? formatDateTime(lead.refusedAt || lead.refused_at || lead.statusUpdatedAt || lead.status_updated_at || lead.updatedAt || lead.updated_at) : null
    },
    {
      key: 'visit',
      label: 'Visite planifiée',
      icon: Calendar,
      color: 'text-purple-600',
      at: lead.visitScheduled ? formatDateTime(lead.visitDate) : null
    }
  ].filter((step) => step.at);

  return (
    <Layout
      headerConfig={{
        icon: profileClientIcon,
        iconColor: 'bg-white',
        title: lead.seekerName,
        subtitle: `Reçu le ${new Date(lead.createdAt || lead.created_at).toLocaleDateString()}`,
        primaryAction: 'Retour',
        onPrimaryAction: () => navigate('/professional/leads'),
        secondaryAction: undefined,
      }}
    >
      <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-500">

        {/* --- 1. BANDEAU D'ACTIONS (Sticky Header Concept) --- */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 sticky top-4 z-10">
          <div className="flex items-center gap-3">
            <div className={cn("px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2", 
              lead.professionalStatus === 'ACCEPTED' ? "bg-green-100 text-green-700" : 
              lead.professionalStatus === 'REFUSED' ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
            )}>
              {lead.professionalStatus === 'ACCEPTED' ? <CheckCircle className="h-4 w-4" /> : 
               lead.professionalStatus === 'REFUSED' ? <XCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
              {lead.professionalStatus === 'ACCEPTED' ? 'Lead Accepté' : lead.professionalStatus === 'REFUSED' ? 'Lead Refusé' : 'En attente de réponse'}
            </div>
            {/* Score IA */}
            {/* {lead.aiScore && (
              <div className="hidden md:flex px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-sm font-semibold items-center gap-2 border border-purple-100">
                <Bot className="h-4 w-4" />
                Score IA: {lead.aiScore}/100
              </div>
            )} */}
          </div>
        </div>

        {/* --- 2. BRIEFING IA (Insight Card) --- */}
        {/* <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-5 border border-purple-100 flex gap-4 items-start">
          <div className="bg-white p-2 rounded-xl shadow-sm flex-shrink-0">
            <Bot className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-purple-900 mb-1">L'analyse de Cynthia</h3>
            <p className="text-sm text-purple-800 leading-relaxed">
              {lead.aiAnalysis}
            </p>
          </div>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* --- COLONNE GAUCHE : LE PROSPECT (2/3) --- */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Carte Infos Client + Projet */}
            <Card className="rounded-2xl border-gray-100 shadow-sm overflow-hidden">
              <CardHeader className="bg-gray-50/50 border-b border-gray-100 pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-5 w-5 text-[#FF9F68]" /> Profil du Prospect
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                
                      <div>
                        <p className="font-bold text-gray-900">{lead.seekerName}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-gray-400" /> 
                        <span className="text-gray-700 select-all">{lead.seekerEmail}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-gray-400" /> 
                        <span className="text-gray-700 select-all">{lead.seekerPhone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Critères du projet</h4>
                    {lead.moveInDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Emménagement :</span>
                        <span className="font-medium">
                          {new Date(lead.moveInDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                    {/* Ajoutez d'autres critères ici (Budget, Financement...) */}
                    <Separator className="bg-gray-200"/>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Message du client :</p>
                      <p className="text-sm text-gray-700 italic">"{lead.seekerMessage}"</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Carte du Bien (Réutilisation du style Client) */}
            {property && (
              <Card className="overflow-hidden border-none shadow-md rounded-2xl group">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={property.images?.[0] || "/placeholder-property.jpg"}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-6 text-white">
                    <p className="text-xs font-medium bg-white/20 backdrop-blur-md px-2 py-1 rounded-md inline-block mb-2">Bien d'intérêt</p>
                    <h3 className="text-xl font-bold truncate">{property.title}</h3>
                    <p className="flex items-center text-gray-200 text-sm mt-1">
                      <MapPin className="h-3 w-3 mr-1" /> {property.city}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* --- COLONNE DROITE : WORKFLOW (1/3) --- */}
          <div className="space-y-6">
            
            {/* Carte Gestion Visite */}
            <Card className="rounded-2xl border-gray-100 shadow-sm transition-all">
              <CardHeader className="pb-3 border-b border-gray-100">
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#FF9F68]" /> Planification Visite
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {isPending && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">Acceptez le lead pour planifier une visite.</p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleStatusChange('REFUSED')}
                        disabled={responding}
                      >
                        <X className="h-4 w-4 mr-2" /> Refuser
                      </Button>
                      <Button 
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleStatusChange('ACCEPTED')}
                        disabled={responding}
                      >
                        <Check className="h-4 w-4 mr-2" /> Accepter
                      </Button>
                    </div>
                  </div>
                )}

                {isRefused && (
                  <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-center">
                    <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                    <p className="font-bold text-red-900 text-sm">Lead refusé</p>
                    <p className="text-red-700 text-sm mt-1">Aucune planification de visite n’est nécessaire.</p>
                  </div>
                )}

                {isAccepted && (
                  <>
                    {lead.visitScheduled ? (
                      <div className="bg-green-50 p-4 rounded-xl border border-green-100 text-center">
                        <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                        <p className="font-bold text-green-900 text-sm">Visite Confirmée</p>
                        <p className="text-green-700 text-sm mt-1">
                      Le {new Date(lead.visitDate).toLocaleDateString()} à {new Date(lead.visitDate).toLocaleTimeString([], { hour: '2-digit', minute:'2-digit', hour12: false })}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 text-center space-y-2">
                        <Clock className="h-8 w-8 text-amber-600 mx-auto" />
                        <p className="font-bold text-amber-900 text-sm">Lead accepté</p>
                        <p className="text-amber-800 text-sm">
                          {visitStatus === 'PENDING_CLIENT'
                            ? "Créneau proposé au client. En attente de confirmation."
                            : "En attente que l’admin choisisse un créneau de visite avec le client."}
                        </p>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-amber-800 h-auto p-0"
                          onClick={() => navigate('/professional/messages')}
                        >
                          Voir la conversation
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Carte Historique Rapide */}
            <Card className="rounded-2xl border-gray-100 shadow-sm bg-gray-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase text-gray-500">Timeline</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="border-l-2 border-gray-200 ml-1 space-y-4 pl-4 py-2">
                  {timelineSteps.map((step, idx) => (
                    <div className="relative" key={step.key}>
                      <div className={`w-2 h-2 rounded-full absolute -left-[21px] top-1.5 ring-4 ring-gray-50 ${step.color.replace('text', 'bg')}`}></div>
                      <p className="text-xs font-medium text-gray-900 flex items-center gap-2">
                        {React.createElement(step.icon, { className: `h-3.5 w-3.5 ${step.color}` })}
                        {step.label}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {step.at?.date} • {step.at?.time}
                      </p>
                    </div>
                  ))}
                  {timelineSteps.length === 0 && (
                    <p className="text-xs text-gray-500">Aucune étape disponible.</p>
                  )}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfessionalLeadDetailPage;